import { Control, Controller } from "react-hook-form";
import ReactSelect, { CSSObjectWithLabel, StylesConfig } from "react-select";
import { Text } from "./Text";
import { useLanguage } from "../../Store/modules/language";
import { useTranslation } from "react-i18next";
import { useThemeColors } from "../../Store/modules/theme";
import { ArrowDown2 } from "iconsax-react";
import { Icon } from "./Icon";
import styled from "../../StyleSheet/styled-components";

export type SelectProps<T> = {
  control: Control<any>;
  name: string;
  iconClassName?: string;
  fontSize?: string | number;
  options: T[];
  isMulti?: boolean;
  styles?: CSSObjectWithLabel;
  disabled?: boolean;
  loading?: boolean;
  height?: number;
  iconSize?: number;
  dir?: "rtl" | "ltr";
};

export function Select<T>(props: SelectProps<T>) {
  const {
    control,
    name,
    loading,
    options,
    isMulti,
    height,
    disabled,
    iconSize = 24,
    iconClassName = "mx-[15px]",
    fontSize = 16,
    dir,
  } = props;

  const { locale } = useLanguage();
  const { t } = useTranslation();
  const [theme] = useThemeColors();

  const customStyles: StylesConfig = {
    valueContainer: (rest) => ({
      ...rest,
      fontFamily: locale === "en" ? "Mulish, Estedad" : "Estedad, Mulish",
    }),
    container: (rest) => ({
      ...rest,
      fontFamily: locale === "en" ? "Mulish, Estedad" : "Estedad, Mulish",
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      display: "none", // This removes the divider line
    }),
    control: (rest) => ({
      ...rest,
      width: "100%",
      fontFamily: locale === "en" ? "Mulish, Estedad" : "Estedad, Mulish",
      height,
      borderRadius: 3,
      borderColor: theme["optBorderInput"],
      padding: "2px 0",
      direction: dir ?? (locale === "fa" ? "rtl" : "ltr"),
      background: theme["inputBg"],
      color: theme["text"],
    }),
    input: (base) => ({
      ...base,
      fontFamily: locale === "en" ? "Mulish, Estedad" : "Estedad, Mulish",
    }),
    singleValue: (rest) => ({
      ...rest,
      color: theme["text"],
      fontFamily: locale === "en" ? "Mulish, Estedad" : "Estedad, Mulish",
    }),
    placeholder: (rest) => ({ ...rest, fontSize, color: "#9EA3AE" }),
    option: (base, state) => ({
      ...base,
      fontFamily: locale === "en" ? "Mulish, Estedad" : "Estedad, Mulish",
    }),
  };

  const StyledWrapper = styled.div`
    font-family: ${locale === "en" ? "Mulish, Estedad" : "Estedad, Mulish"} !important;
  `;

  return (
    <StyledWrapper>
      <Controller
        render={({ field }) => (
          <ReactSelect
            value={field.value}
            onChange={(val) => field.onChange(val)}
            className="xs:w-full"
            components={{
              DropdownIndicator: () => (
                <Icon
                  Component={ArrowDown2}
                  size={iconSize}
                  color="text"
                  className={iconClassName}
                />
              ),
            }}
            isDisabled={disabled}
            loadingMessage={() => (
              <Text elementType="span" fontSize={fontSize}>
                لطفا صبر کنید...
              </Text>
            )}
            isLoading={loading}
            styles={customStyles}
            //@ts-ignore
            placeholder={t("select")}
            options={options}
            isMulti={isMulti}
            noOptionsMessage={() => {
              return (
                <Text elementType="span" fontSize={fontSize}>
                  گزینه ای وجود ندارد.
                </Text>
              );
            }}
          />
        )}
        name={name}
        control={control}
      />
    </StyledWrapper>
  );
}
